import { SwitchProfileMutation } from "#src/api/Auth.ts";
import ItemCard from "#src/components/items/item/ItemCard.tsx";
import Modal, { ModalProps } from "#src/components/modals/Modal.tsx";
import UserModel from "#src/models/UserModel.ts";
import { apiClient } from "#src/stores/Api.tsx";
import { useStore } from "#src/stores/Store.tsx";
import { setToken } from "#src/utils/getToken.ts";
import { modals, useModal } from "#src/utils/modals.ts";
import tw from "#src/utils/tw.ts";
import { Level } from "#types/code-generator.ts";

interface Props extends ModalProps {}

export const ProfileSwitchModal = modals.create((_props: Props) => {
  const modal = useModal();
  const store = useStore();

  async function switchProfile(profile: {
    organization: {
      id: string;
      subdomain: string;
    };
  }) {
    const switchProfileQuery = await apiClient(SwitchProfileMutation, {
      variables: {
        organizationId: profile.organization.id,
      },
    });

    if (!switchProfileQuery.switchProfile) {
      throw new Error("Unable to switch profile");
    }

    setToken(switchProfileQuery.switchProfile.token);

    let url = `https://${profile.organization.subdomain}.captyn.com`;

    if (process.env.NODE_ENV === "development") {
      url = `http://${profile.organization.subdomain}.local.localhost:3000`;
    } else if (process.env.NEXT_PUBLIC_STAGE === "demo") {
      url = `https://${profile.organization.subdomain}.nytpac.com`;
    }

    window.location.assign(url);
  }

  const profiles = [
    {
      ...store.user,
      organization: store.organization,
    },
    ...store.user.profile.profiles.filter(
      (p) => p.organization.id !== store.organization.id
    ),
  ];

  return (
    <Modal
      visible={modal.visible}
      title="Switch Organization"
      afterClose={modal.remove}
      handleCancel={modal.hide}
    >
      {profiles.map((p) => {
        const user = new UserModel(p);
        const isCurrentOrg = p.organization.id === store.organization.id;
        let level = p.level.toString();

        if (user.isAdminOrAbove) {
          level = store.organization.terminology.administrator;
        } else if (user.isInstructor) {
          level = store.organization.terminology.staff;
        } else if (user.level === Level.AccountOwner) {
          level = "Customer";
        }

        return (
          <div key={p.id}>
            <ItemCard
              className="hover:border-darkGray flex cursor-pointer items-center gap-2 overflow-hidden border-b-3 p-0"
              onClick={isCurrentOrg ? undefined : () => switchProfile(p)}
            >
              <div
                className={tw(
                  "flex h-16 w-16 items-center justify-center",
                  !p.organization.logo && "bg-organization",
                  p.organization.logo && "border-r border-borderGray"
                )}
              >
                {p.organization.logo && (
                  <img
                    src={p.organization.logo.url}
                    className="h-full w-full object-cover"
                  />
                )}
                {!p.organization.logo && (
                  <i className="far fa-medal text-xl text-white opacity-50" />
                )}
              </div>
              <div className="flex-1 leading-tight">
                <strong className="text-organization">
                  {p.organization.title}
                </strong>
                <div className="text-sm">{level}</div>
              </div>
            </ItemCard>

            {isCurrentOrg && <hr />}
          </div>
        );
      })}
    </Modal>
  );
});
